import { baseApi } from "./baseApi";
import { API_ENDPOINTS } from "../../config/url";

// Types for Product API
export interface ProductItem {
  _id?: string;
  id?: string;
  image?: string;
  name?: string;
  ingredients?: string;
  description?: string;
  type?: number;
  variant?: number;
  size?: number;
  price?: number;
  doublePrice?: number;
  isActive?: boolean;
  featured?: boolean;
  createdAt?: string;
  categoryDetails?: {
    _id?: string;
    name?: string;
    image?: string;
  };
  [key: string]: any;
}

export interface GetProductListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetProductListResponse {
  data?: {
    product?: ProductItem[];
    total?: number;
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  totalCount?: number;
  totalPages?: number;
  currentPage?: number;
  [key: string]: any;
}

export interface CreateProductRequest {
  image: string;
  categoryId: string;
  name: string;
  ingredients: string;
  description: string;
  type: number; // 1 for SIZE, 2 for VARIANT
  variant?: number; // 1 for SINGLE, 2 for DOUBLE (only when type is 2)
  size?: number; // Only when type is 1
  price: number; // Single price or price for size
  doublePrice?: number; // Only when variant is 2 (DOUBLE)
}

export interface CreateProductResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateProductRequest {
  id: string;
  image?: string;
  categoryId?: string;
  name?: string;
  ingredients?: string;
  description?: string;
  type?: number;
  variant?: number;
  size?: number;
  price?: number;
  doublePrice?: number;
  isActive?: boolean;
}

export interface UpdateProductResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface DeleteProductRequest {
  id: string;
}

export interface DeleteProductResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateProductStatusRequest {
  id: string;
  isActive: boolean;
}

export interface UpdateProductStatusResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateProductFeaturedRequest {
  id: string;
  featured: boolean;
  isActive: boolean;
}

export interface UpdateProductFeaturedResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Types for Category API
export interface CategoryItem {
  _id?: string;
  id?: string;
  name?: string;
  image?: string;
  isBlocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  subcategories?: any[];
  subcategoryCount?: number;
  [key: string]: any;
}

export interface GetCategoryListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetCategoryListResponse {
  data?: {
    list?: CategoryItem[];
    totalCount?: number;
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface CreateCategoryRequest {
  name: string;
  image: string;
}

export interface CreateCategoryResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateCategoryRequest {
  id: string;
  name?: string;
  image?: string;
  parentId?: string;
}

export interface UpdateCategoryResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface DeleteCategoryRequest {
  id: string;
}

export interface DeleteCategoryResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateCategoryStatusRequest {
  id: string;
  isBlocked: boolean;
}

export interface UpdateCategoryStatusResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Types for Store API
export interface StoreAddress {
  fullAddress?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
}

export interface StoreAvailabilityWeekday {
  isActive?: boolean;
  startTime?: string;
  endTime?: string;
  day?: number;
}

export interface StoreAvailability {
  weekday1?: StoreAvailabilityWeekday;
  weekday2?: StoreAvailabilityWeekday;
  weekday3?: StoreAvailabilityWeekday;
  weekday4?: StoreAvailabilityWeekday;
  weekday5?: StoreAvailabilityWeekday;
  weekday6?: StoreAvailabilityWeekday;
  weekday7?: StoreAvailabilityWeekday;
}

export interface StoreItem {
  _id?: string;
  id?: string;
  image?: string;
  name?: string;
  code?: string;
  address?: StoreAddress;
  region?: string;
  isBlocked?: boolean;
  createdAt?: string;
  storeAvailability?: StoreAvailability;
  [key: string]: any;
}

export interface GetStoreListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetStoreListResponse {
  data?: {
    store?: StoreItem[];
    total?: number;
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface GetStoreDetailRequest {
  id: string;
}

export interface StoreDetailData {
  data?: StoreItem[];
  storeCategory?: Array<{
    _id?: string;
    categoryDetails?: {
      _id?: string;
      name?: string;
    };
  }>;
  storeProduct?: Array<{
    _id?: string;
    productDetails?: {
      _id?: string;
      name?: string;
    };
  }>;
  storeFlavor?: Array<{
    _id?: string;
    flavorDetails?: {
      _id?: string;
      name?: string;
    };
  }>;
  storeTopping?: Array<{
    _id?: string;
    toppingDetails?: {
      _id?: string;
      name?: string;
    };
  }>;
  [key: string]: any;
}

export interface GetStoreDetailResponse {
  data?: StoreDetailData;
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface StoreWeekday {
  isActive?: boolean;
  startTime?: string;
  endTime?: string;
}

export interface CreateStoreRequest {
  image: string;
  name: string;
  code: string;
  flavorId?: string[];
  toppingId?: string[];
  categories?: Array<{
    categoryId: string;
    productId: string[];
  }>;
  address: {
    fullAddress: string;
    street: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  };
  region: string;
  latitude: number;
  longitude: number;
  weekday1?: StoreWeekday;
  weekday2?: StoreWeekday;
  weekday3?: StoreWeekday;
  weekday4?: StoreWeekday;
  weekday5?: StoreWeekday;
  weekday6?: StoreWeekday;
  weekday7?: StoreWeekday;
}

export interface CreateStoreResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface DeleteStoreRequest {
  id: string;
}

export interface DeleteStoreResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Types for Store Inventory API
export interface InventoryItem {
  _id?: string;
  id?: string;
  name?: string;
  image?: string;
  category?: string;
  categoryId?: string;
  categoryDetails?: {
    _id?: string;
    name?: string;
    image?: string;
  };
  isOutOfStock?: boolean;
  [key: string]: any;
}

export interface GetStoreInventoryRequest {
  type: "topping" | "flavor" | "product" | "boxart";
  storeId: string;
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
}

export interface GetStoreInventoryResponse {
  data?: {
    list?: InventoryItem[];
    total?: number;
    totalCount?: number;
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface UpdateStoreInventoryStatusRequest {
  type: "topping" | "flavor" | "product" | "boxart";
  itemId: string;
  isActive: boolean;
}

export interface UpdateStoreInventoryStatusResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateStoreRequest {
  id: string;
  image?: string;
  name?: string;
  code?: string;
  flavorId?: string[];
  toppingId?: string[];
  categories?: Array<{
    categoryId: string;
    productId: string[];
  }>;
  address?: {
    fullAddress?: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
    latitude?: number;
    longitude?: number;
  };
  region?: string;
  latitude?: number;
  longitude?: number;
  weekday1?: StoreWeekday;
  weekday2?: StoreWeekday;
  weekday3?: StoreWeekday;
  weekday4?: StoreWeekday;
  weekday5?: StoreWeekday;
  weekday6?: StoreWeekday;
  weekday7?: StoreWeekday;
}

export interface UpdateStoreResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateStoreStatusRequest {
  id: string;
  isBlocked: boolean;
}

export interface UpdateStoreStatusResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// CMS Types
export interface CMSContactSupport {
  email?: string;
  phone?: string;
  countryCode?: string;
}

export interface CMSData {
  _id?: string;
  contactSupport?: CMSContactSupport;
  legal?: string;
  privacyPolicy?: string;
  dataDeletionPolicy?: string;
  aboutUs?: string;
  termsAndConditions?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetCMSResponse {
  data?: CMSData;
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface UpdateCMSRequest {
  contactSupport?: CMSContactSupport;
  legal?: string;
  privacyPolicy?: string;
  dataDeletionPolicy?: string;
  aboutUs?: string;
  termsAndConditions?: string;
}

export interface UpdateCMSResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Dispatcher Types
export interface DispatcherItem {
  _id?: string;
  id?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  image?: string;
  dateOfBirth?: string;
  gender?: number;
  role?: number;
  isVerified?: boolean;
  isBlocked?: boolean;
  isDeleted?: boolean;
  isProfileComplete?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  isPasswordSet?: boolean;
  status?: number;
  createdBy?: number;
  storeName?: string;
  bio?: string;
  openingTime?: string;
  closingTime?: string;
  isOnline?: boolean;
  bankDetails?: {
    bankName?: string;
    accHolderName?: string;
    branchName?: string;
    bankCode?: string;
  };
  document?: {
    name?: string;
    expiryDate?: string;
    frontImage?: string;
    backImage?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  [key: string]: any;
}

export interface GetDispatcherListRequest {
  page?: number;
  limit?: number;
  search?: string;
  status?: number;
  isDropdown?: boolean;
}

export interface GetDispatcherListResponse {
  data?: DispatcherItem[] | {
    list?: DispatcherItem[];
    totalCount?: number;
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  totalCount?: number;
  totalPages?: number;
  currentPage?: number;
  [key: string]: any;
}

export interface UpdateDispatcherStatusRequest {
  id: string;
  isBlocked: boolean;
}

export interface UpdateDispatcherStatusResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface DeleteDispatcherRequest {
  id: string;
}

export interface DeleteDispatcherResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface DispatcherAddress {
  addressLine1?: string;
  addressLine2?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state?: string;
  country?: string;
  pinCode?: string;
  type?: number;
  isDefault?: boolean;
}

export interface DispatcherBankDetails {
  bankName?: string;
  accHolderName?: string;
  branchName?: string;
  bankCode?: string;
}

export interface DispatcherDocument {
  name?: string;
  expiryDate?: string;
  frontImage?: string;
  backImage?: string;
}

export interface CreateDispatcherRequest {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  image?: string;
  dateOfBirth?: string;
  gender?: number;
  address?: DispatcherAddress;
  bankDetails?: DispatcherBankDetails;
  document?: DispatcherDocument;
  isOnline?: boolean;
  storeName?: string;
  bio?: string;
  openingTime?: string;
  closingTime?: string;
}

export interface CreateDispatcherResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface UpdateDispatcherRequest {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  image?: string;
  dateOfBirth?: string;
  gender?: number;
  address?: DispatcherAddress;
  bankDetails?: DispatcherBankDetails;
  document?: DispatcherDocument;
  isOnline?: boolean;
  storeName?: string;
  bio?: string;
  openingTime?: string;
  closingTime?: string;
}

export interface UpdateDispatcherResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Revenue Analytics Types
export interface RevenueAnalyticsItem {
  label: string;
  value: number;
}

export interface GetRevenueAnalyticsRequest {
  type: string; // 'revenue' | 'order'
  period: string; // 'day' | 'month' | 'year'
  startDate?: string;
  endDate?: string;
}

export interface GetRevenueAnalyticsResponse {
  data: {
    data: RevenueAnalyticsItem[];
  };
  message: string;
  success?: boolean;
}

// Ratings Types
export interface RatingItem {
  _id?: string;
  userId?: string;
  orderId?: string;
  ratingFor?: string;
  score?: number;
  reviews?: string;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
  user?: {
    _id?: string;
    fullName?: string;
    email?: string;
    phone?: string;
    countryCode?: string;
    image?: string;
  };
  order?: {
    _id?: string;
    status?: number;
    grandTotal?: number;
  };
  [key: string]: any;
}

export interface GetRatingsListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetRatingsListResponse {
  data?: {
    list?: RatingItem[];
    total?: number;
  };
  message?: string;
  success?: boolean;
}

export interface GetSubAdminListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface SubAdminItem {
  _id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  image: string;
  gender: number;
  role: number;
  accessRole?: any;
  storeId?: string;
  isVerified: boolean;
  isBlocked: boolean;
  isProfileComplete: boolean;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isPasswordSet: boolean;
  status: number;
  createdBy: number;
  loyaltyPointsEarned: number;
  loyaltyPointsRedeemed: number;
  loyaltyPointsBalance: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetSubAdminListResponse {
  data?: {
    list?: SubAdminItem[];
    totalCount?: number;
  };
  message?: string;
  success?: boolean;
}

export interface GetSubAdminDetailRequest {
  id: string;
}

export interface GetSubAdminDetailResponse {
  data?: SubAdminItem;
  message?: string;
  success?: boolean;
}

export interface Permission {
  sideBarId: number;
  label: string;
  path: string;
  isView: boolean;
  isAdd: boolean;
  isEdit: boolean;
  isDelete: boolean;
  _id?: string;
  subMenuItems?: Permission[];
}

export interface CreateRoleRequest {
  name: string;
  permissions: Permission[];
}

export interface CreateRoleResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface UpdateRoleRequest {
  id: string;
  name: string;
  isBlocked?: boolean;
  permissions: Permission[];
}

export interface UpdateRoleResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface DeleteRoleRequest {
  id: string;
}

export interface DeleteRoleResponse {
  message?: string;
  success?: boolean;
  [key: string]: any;
}


export interface RoleItem {
  _id: string;
  name: string;
  isBlocked: boolean;
  permissions: Permission[];
  createdAt: string;
  updatedAt: string;
}

export interface GetRolesListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetRolesListResponse {
  data?: {
    list?: RoleItem[];
    totalCount?: number;
  };
  message?: string;
  success?: boolean;
}

export interface GetRoleDetailRequest {
  id: string;
}

export interface GetRoleDetailResponse {
  data?: RoleItem;
  message?: string;
  success?: boolean;
}

export interface DeleteRatingRequest {
  id: string;
}

export interface DeleteRatingResponse {
  message?: string;
  success?: boolean;
  data?: any;
}



// Dashboard Types
export interface DashboardData {
  totalUsers: number;
  usersJoinedToday: number;
  loyaltyPointsIssued: number;
  totalOrders: number;
  ordersToday: number;
  completedOrders: number;
  totalFlavors: number;
  totalToppings: number;
}

export interface GetDashboardResponse {
  data?: DashboardData;
  message?: string;
  success?: boolean;
}

export interface GraphDataPoint {
  label: string;
  value: number;
}

export interface GetGraphDataRequest {
  type: "orders" | "revenue"; // user specified 'order' and 'revenue'
  period: string;
}

export interface GetGraphDataResponse {
  data?: {
    data: GraphDataPoint[];
  };
  message?: string;
  success?: boolean;
}

// FAQ Types
export interface FaqItem {
  _id: string;
  question: string;
  answer: string;
  isBlocked: boolean;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface GetFaqListRequest {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetFaqListResponse {
  data?: {
    list?: FaqItem[];
    totalCount?: number;
  };
  message?: string;
  success?: boolean;
}

export interface CreateFaqRequest {
  question: string;
  answer: string;
}

export interface CreateFaqResponse {
  message?: string;
  success?: boolean;
  data?: any;
}

export interface UpdateFaqRequest {
  id: string;
  question?: string;
  answer?: string;
  isBlocked?: boolean;
}

export interface UpdateFaqResponse {
  message?: string;
  success?: boolean;
  data?: any;
}

export interface DeleteFaqRequest {
  id: string;
}

export interface DeleteFaqResponse {
  message?: string;
  success?: boolean;
  data?: any;
}

export const manageAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET - Ratings List
    getRatingsList: builder.query<GetRatingsListResponse, GetRatingsListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.RATINGS.LIST,
        method: "GET",
        params: {
          page,
          limit,
          search,
        },
        headers: {
          "x-portal": "admin",
        },
      }),
      providesTags: ["RATING"], // Ensure "Rating" tag is added to tagTypes in baseApi if not present, but for now it might just work if baseApi is loose or I can add it.
    }),



    // DELETE - Delete Rating
    deleteRating: builder.mutation<DeleteRatingResponse, DeleteRatingRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.RATINGS.DELETE(id),
        method: "DELETE",
        body: { ratingId: id },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      invalidatesTags: ["RATING"],
    }),

    // GET - Revenue Analytics
    getRevenueAnalytics: builder.query<GetRevenueAnalyticsResponse, GetRevenueAnalyticsRequest>({
      query: (params) => ({
        url: API_ENDPOINTS.REVENUE.ANALYTICS,
        method: "GET",
        params,
        headers: {
          "x-portal": "admin",
        }
      }),
    }),

    // GET - Get Product List
    getProductList: builder.query<GetProductListResponse, GetProductListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.PRODUCT.LIST,
        method: "GET",
        params: {
          page,
          limit,
          search,
        },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      providesTags: ["Product"],
    }),

    // POST - Create Product
    createProduct: builder.mutation<CreateProductResponse, CreateProductRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.PRODUCT.CREATE,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Product"],
    }),

    // PUT - Update Product
    updateProduct: builder.mutation<UpdateProductResponse, UpdateProductRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.PRODUCT.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Product"],
    }),

    // DELETE - Delete Product
    deleteProduct: builder.mutation<DeleteProductResponse, DeleteProductRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.PRODUCT.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Product"],
    }),

    // PUT - Update Product Status
    updateProductStatus: builder.mutation<UpdateProductStatusResponse, UpdateProductStatusRequest>({
      query: ({ id, isActive }) => ({
        url: API_ENDPOINTS.PRODUCT.UPDATE(id),
        method: "PUT",
        body: { isActive },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Product"],
    }),

    // PUT - Update Product Featured Status
    updateProductFeatured: builder.mutation<UpdateProductFeaturedResponse, UpdateProductFeaturedRequest>({
      query: ({ id, featured, isActive }) => ({
        url: API_ENDPOINTS.PRODUCT.UPDATE(id),
        method: "PUT",
        body: { featured, isActive },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Product"],
    }),

    // GET - Get Category List
    getCategoryList: builder.query<GetCategoryListResponse, GetCategoryListRequest>({
      query: ({ page = 1, limit = 10 }) => {
        // Build query string manually
        const queryString = `?page=${page}&limit=${limit}`;

        return {
          url: `${API_ENDPOINTS.CATEGORY.LIST}${queryString}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-portal": "user",
          },
        };
      },
      providesTags: ["Category"],
    }),

    // POST - Create Category
    createCategory: builder.mutation<CreateCategoryResponse, CreateCategoryRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.CATEGORY.CREATE,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      invalidatesTags: ["Category"],
    }),

    // PUT - Update Category
    updateCategory: builder.mutation<UpdateCategoryResponse, UpdateCategoryRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.CATEGORY.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Category"],
    }),

    // DELETE - Delete Category
    deleteCategory: builder.mutation<DeleteCategoryResponse, DeleteCategoryRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.CATEGORY.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Category"],
    }),

    // PUT - Update Category Status
    updateCategoryStatus: builder.mutation<UpdateCategoryStatusResponse, UpdateCategoryStatusRequest>({
      query: ({ id, isBlocked }) => ({
        url: API_ENDPOINTS.CATEGORY.UPDATE(id),
        method: "PUT",
        body: { isBlocked },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Category"],
    }),

    // GET - Get Store List
    getStoreList: builder.query<GetStoreListResponse, GetStoreListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.STORE.LIST,
        method: "GET",
        params: {
          page,
          limit,
          search,
        },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      providesTags: ["Store"],
    }),

    // POST - Create Store
    createStore: builder.mutation<CreateStoreResponse, CreateStoreRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.STORE.LIST,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Store"],
    }),

    // GET - Get Store Detail
    getStoreDetail: builder.query<GetStoreDetailResponse, GetStoreDetailRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.STORE.DETAIL(id),
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      providesTags: ["Store"],
    }),

    // PUT - Update Store
    updateStore: builder.mutation<UpdateStoreResponse, UpdateStoreRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.STORE.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Store"],
    }),

    // PUT - Update Store Status (Enable/Disable)
    updateStoreStatus: builder.mutation<UpdateStoreStatusResponse, UpdateStoreStatusRequest>({
      query: ({ id, isBlocked }) => ({
        url: API_ENDPOINTS.STORE.UPDATE(id),
        method: "PUT",
        body: { isBlocked },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Store"],
    }),

    // DELETE - Delete Store
    deleteStore: builder.mutation<DeleteStoreResponse, DeleteStoreRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.STORE.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Store"],
    }),

    // GET - Get Store Inventory
    getStoreInventory: builder.query<GetStoreInventoryResponse, GetStoreInventoryRequest>({
      query: ({ type, storeId, page = 1, limit = 10, search, categoryId }) => {
        const params = new URLSearchParams();
        params.append("type", type);
        params.append("storeId", storeId);
        params.append("page", page.toString());
        params.append("limit", limit.toString());
        if (search) {
          params.append("search", search);
        }
        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        return {
          url: `${API_ENDPOINTS.STORE.INVENTORY}?${params.toString()}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-portal": "user",
          },
        };
      },
      providesTags: (result, error, arg) => [
        { type: "Store", id: `inventory-${arg.type}-${arg.storeId}` },
        "Store",
      ],
    }),

    // PUT - Update Store Inventory Status
    updateStoreInventoryStatus: builder.mutation<UpdateStoreInventoryStatusResponse, UpdateStoreInventoryStatusRequest>({
      query: ({ type, itemId, isActive }) => {
        // Map type to the correct field name in the body
        const fieldNameMap: Record<string, string> = {
          topping: "toppingId",
          flavor: "flavorId",
          product: "productId",
          boxart: "boxartId",
        };

        const fieldName = fieldNameMap[type] || "itemId";

        return {
          url: API_ENDPOINTS.STORE.MANAGE_INVENTORY,
          method: "PUT",
          body: {
            [fieldName]: itemId,
            isActive: isActive,
          },
          headers: {
            "Content-Type": "application/json",
            "x-portal": "user",
          },
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Store", id: `inventory-${arg.type}-*` },
        "Store",
      ],
    }),

    // GET - Get CMS Data
    getCMS: builder.query<GetCMSResponse, void>({
      query: () => ({
        url: API_ENDPOINTS.CMS.GET,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      providesTags: ["CMS"],
    }),

    // PUT - Update CMS Data
    updateCMS: builder.mutation<UpdateCMSResponse, UpdateCMSRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.CMS.UPDATE,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["CMS"],
    }),

    // GET - Get Dispatcher List
    getDispatcherList: builder.query<GetDispatcherListResponse, GetDispatcherListRequest>({
      query: ({ page = 1, limit = 10, search = "", status = 2, isDropdown = false }) => {
        const params: any = {
          status,
          isDropdown,
        };
        if (search) {
          params.search = search;
        }
        if (page) {
          params.page = page;
        }
        if (limit) {
          params.limit = limit;
        }
        return {
          url: API_ENDPOINTS.DISPATCHER.LIST,
          method: "GET",
          params,
          headers: {
            "Content-Type": "application/json",
            "x-portal": "user",
          },
        };
      },
      providesTags: ["Dispatcher"],
    }),

    // PUT - Update Dispatcher Status (Enable/Disable)
    updateDispatcherStatus: builder.mutation<UpdateDispatcherStatusResponse, UpdateDispatcherStatusRequest>({
      query: ({ id, isBlocked }) => ({
        url: API_ENDPOINTS.DISPATCHER.UPDATE(id),
        method: "PUT",
        body: { isBlocked },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Dispatcher"],
    }),

    // DELETE - Delete Dispatcher
    deleteDispatcher: builder.mutation<DeleteDispatcherResponse, DeleteDispatcherRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.DISPATCHER.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Dispatcher"],
    }),


    // POST - Create Dispatcher
    createDispatcher: builder.mutation<CreateDispatcherResponse, CreateDispatcherRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.DISPATCHER.CREATE,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Dispatcher"],
    }),

    // GET - Sub-Admin List
    getSubAdminList: builder.query<GetSubAdminListResponse, GetSubAdminListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.MANAGE_SUB_ADMIN.LIST,
        method: "GET",
        params: {
          page,
          limit,
          search,
        },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      providesTags: ["SubAdmin"],
    }),

    // POST - Create Sub-Admin
    createSubAdmin: builder.mutation<any, any>({
      query: (data) => ({
        url: API_ENDPOINTS.MANAGE_SUB_ADMIN.LIST,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    // DELETE - Sub-Admin
    deleteSubAdmin: builder.mutation<any, string>({
      query: (id) => ({
        url: API_ENDPOINTS.MANAGE_SUB_ADMIN.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    // PUT - Update Sub-Admin
    updateSubAdmin: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.MANAGE_SUB_ADMIN.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    // GET - Sub-Admin Detail
    getSubAdminDetail: builder.query<GetSubAdminDetailResponse, GetSubAdminDetailRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.MANAGE_SUB_ADMIN.DETAIL(id),
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      providesTags: ["SubAdmin"],
    }),



    // GET - Roles List
    getRolesList: builder.query<GetRolesListResponse, GetRolesListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.MANAGE_ROLES.LIST,
        method: "GET",
        params: {
          page,
          limit,
          search,
        },
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      providesTags: ["SubAdmin"], // Or "Role" if I add it, but SubAdmin seems related
    }),

    // GET - Role Detail
    getRoleDetail: builder.query<GetRoleDetailResponse, GetRoleDetailRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.MANAGE_ROLES.DETAIL(id),
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      providesTags: ["SubAdmin"],
    }),

    // PUT - Update Dispatcher
    updateDispatcher: builder.mutation<UpdateDispatcherResponse, UpdateDispatcherRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.DISPATCHER.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "user",
        },
      }),
      invalidatesTags: ["Dispatcher"],
    }),

    // GET - FAQ List
    getFaqList: builder.query<GetFaqListResponse, GetFaqListRequest>({
      query: ({ page = 1, limit = 10, search = "" }) => ({
        url: API_ENDPOINTS.FAQ.LIST,
        method: "GET",
        params: { page, limit, search },
        headers: { "x-portal": "user" },
      }),
      providesTags: ["FAQ"],
    }),

    // GET - Dashboard Data
    getDashboardData: builder.query<GetDashboardResponse, void>({
      query: () => ({
        url: API_ENDPOINTS.DASHBOARD.GET,
        method: "GET",
        headers: { "x-portal": "admin" },
      }),
    }),

    // GET - Dashboard Graph Data
    getDashboardGraphData: builder.query<GetGraphDataResponse, GetGraphDataRequest>({
      query: ({ type, period }) => ({
        url: `${API_ENDPOINTS.DASHBOARD.GRAPH}?type=${type}&period=${period}`,
        method: "GET",
        headers: { "x-portal": "admin" },
      }),
    }),

    // POST - Create Role
    createRole: builder.mutation<CreateRoleResponse, CreateRoleRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.MANAGE_ROLES.LIST, // Using LIST endpoint for creation based on user input
        method: "POST",
        body: data,
        headers: { "x-portal": "user" }, // User specified x-portal: user
      }),
      invalidatesTags: ["SubAdmin"], // Assuming this affects roles/subadmins
    }),

    // PUT - Update Role
    updateRole: builder.mutation<UpdateRoleResponse, UpdateRoleRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.MANAGE_ROLES.UPDATE(id),
        method: "PUT",
        body: data,
        headers: { "x-portal": "user" },
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    // DELETE - Delete Role
    deleteRole: builder.mutation<DeleteRoleResponse, DeleteRoleRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.MANAGE_ROLES.DELETE(id),
        method: "DELETE",
        headers: { "x-portal": "user" },
      }),
      invalidatesTags: ["SubAdmin"],
    }),

    // POST - Create FAQ
    createFaq: builder.mutation<CreateFaqResponse, CreateFaqRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.FAQ.CREATE,
        method: "POST",
        body: data,
        headers: { "x-portal": "user" },
      }),
      invalidatesTags: ["FAQ"],
    }),

    // PUT - Update FAQ
    updateFaq: builder.mutation<UpdateFaqResponse, UpdateFaqRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.FAQ.UPDATE(id),
        method: "PUT",
        body: data,
        headers: { "x-portal": "user" },
      }),
      invalidatesTags: ["FAQ"],
    }),

    // DELETE - Delete FAQ
    deleteFaq: builder.mutation<DeleteFaqResponse, DeleteFaqRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.FAQ.DELETE(id),
        method: "DELETE",
        headers: { "x-portal": "user" },
      }),
      invalidatesTags: ["FAQ"],
    }),

  }),
});

export const {
  useGetProductListQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useUpdateProductStatusMutation,
  useUpdateProductFeaturedMutation,
  useGetCategoryListQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryStatusMutation,
  useGetStoreListQuery,
  useCreateStoreMutation,
  useGetStoreDetailQuery,
  useUpdateStoreMutation,
  useUpdateStoreStatusMutation,
  useDeleteStoreMutation,
  useGetStoreInventoryQuery,
  useUpdateStoreInventoryStatusMutation,
  useGetCMSQuery,
  useUpdateCMSMutation,
  useGetDispatcherListQuery,
  useUpdateDispatcherStatusMutation,
  useDeleteDispatcherMutation,
  useCreateDispatcherMutation,
  useUpdateDispatcherMutation,
  useGetRevenueAnalyticsQuery,
  useGetRatingsListQuery,
  useDeleteRatingMutation,
  useGetSubAdminListQuery,
  useCreateSubAdminMutation,
  useDeleteSubAdminMutation,
  useUpdateSubAdminMutation,
  useGetSubAdminDetailQuery,
  useGetRolesListQuery,
  useGetRoleDetailQuery,
  useGetFaqListQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetDashboardDataQuery,
  useGetDashboardGraphDataQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = manageAPI;

